import { motion } from "framer-motion";
import {
  FiGlobe,
  FiUsers,
  FiCheckCircle,
  FiArrowRight,
  // FiTarget,
  // FiTrendingUp,
  // FiShield,
  // FiZap,
  // FiAward,
} from "react-icons/fi";

import {
  FaBuilding, FaLightbulb,
  FaHandshake
} from "react-icons/fa";
import { Link } from "react-router-dom";
import "./EngagementModels.css";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.15 } },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
};

const models = [
  {
    theme: "green",
    icon: FaLightbulb,
    title: "Technology Consulting – Outcome-Driven Model",
    description:
      "Our technology consulting approach is rooted in delivering measurable outcomes. We collaborate closely with customers and partners to understand their business challenges, define clear success metrics, and design technology solutions that create real impact.",
    descriptionExtra:
      "Our consultants bring a blend of technical depth and domain expertise, ensuring every engagement is focused on value creation.",
    highlights: [
      "Measurable Outcomes",
      "Business Impact",
      "Domain Expertise",
      "Value Creation",
      "Clear Metrics",
    ],
  },
  {
    theme: "blue",
    icon: FaBuilding,
    title: "Build – Operate – Transfer (BOT) Model",
    description:
      "We help organizations quickly scale by building dedicated teams with skilled and diverse capabilities through our proven recruitment and delivery model.",
    descriptionExtra:
      "We operate the team for a defined period, ensuring seamless execution, knowledge transfer, and alignment with the customer's goals. Once the customer is ready, we transfer the fully functional, well-integrated team with no disruption.",
    highlights: [
      "Rapid Scaling",
      "Dedicated Teams",
      "Knowledge Transfer",
      "Seamless Transition",
      "Proven Model",
    ],
  },
  {
    theme: "teal",
    icon: FiGlobe,
    title: "Offshore Office Model",
    description:
      "SpringUp specializes in setting up customized Offshore Development Centers (ODCs) tailored to our clients' unique needs. We offer flexible engagement models with shared responsibilities in development and delivery.",
    descriptionExtra:
      "Our success is deeply tied to yours — we operate as a true extension of your team, focused on delivering value, efficiency, and innovation from day one.",
    highlights: [
      "Custom ODCs",
      "Flexible Models",
      "Team Extension",
      "Shared Delivery",
      "Day-One Value",
    ],
  },
  {
    theme: "purple",
    icon: FiUsers,
    title: "Staff Augmentation",
    description:
      "SpringUp offers contract-based resourcing solutions to support seamless operations across software development, test automation, and DevOps.",
    descriptionExtra:
      "We specialize in providing skilled professionals with expertise in innovative and niche technologies, tailored to fit your project needs and timelines. Engagements can be structured under either Firm Fixed Price (FFP) or Time & Material (T&M) models.",
    highlights: [
      "Contract-Based",
      "Niche Technologies",
      "FFP & T&M Models",
      "DevOps Expertise",
      "Flexible Timelines",
    ],
  },
];

// const whyItems = [
//   { icon: FiTarget, title: "Focused Outcomes", desc: "Every engagement is designed around measurable business impact." },
//   { icon: FaHandshake, title: "True Partnership", desc: "We operate as an extension of your team, not just a vendor." },
//   { icon: FiTrendingUp, title: "Scalable Models", desc: "Flexible engagement structures that grow with your needs." },
//   { icon: FiShield, title: "Risk Mitigation", desc: "Proven processes and governance to minimize project risk." },
//   { icon: FiZap, title: "Rapid Delivery", desc: "Agile execution ensuring speed without compromising quality." },
//   { icon: FiAward, title: "Domain Expertise", desc: "Deep technical and industry knowledge across diverse domains." },
// ];

const EngagementModels = () => {
  return (
    <div className="em-page">
      {/* Hero */}
      <section className="em-hero">
        <div className="em-hero-circle-1" />
        <div className="em-hero-circle-2" />
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.div variants={fadeUp} className="em-hero-badge">
            <FaHandshake size={16} /> Engagement Models
          </motion.div>
          <motion.h1 variants={fadeUp}>
            Empowering Innovation Through{" "}
            <span>Expert Technology Consulting</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="em-hero-subtitle">
            Digital Engineering through value-driven, customer-focused, and
            design thinking approach. Harness our talent and expertise to
            address complex business needs.
          </motion.p>
        </motion.div>
      </section>

      {/* Models */}
      <section className="em-section">
        <motion.div
          className="em-section-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <h2>Our Engagement Models</h2>
          <p>
            Diverse technology consulting engagements empowering our clients and
            partners to harness cutting-edge digital solutions.
          </p>
        </motion.div>

        <motion.div
          className="em-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={stagger}
        >
          {models.map((m) => {
            const Icon = m.icon;
            return (
              <motion.div
                key={m.title}
                className={`em-card em-card--${m.theme}`}
                variants={scaleIn}
              >
                <div className="em-card-corner em-card-corner-tl" />
                <div className="em-card-corner em-card-corner-br" />
                <div className="em-card-icon">
                  <Icon />
                </div>
                <h3>{m.title}</h3>
                <p>{m.description}</p>
                <p>{m.descriptionExtra}</p>
                <div className="em-highlights-title">Highlights</div>
                <div className="em-highlights-list">
                  {m.highlights.map((h) => (
                    <span key={h} className="em-highlight-chip">
                      <FiCheckCircle /> {h}
                    </span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* Why SpringUp */}
      {/* <section className="em-why-section">
        <motion.div
          className="em-section-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={fadeUp}
        >
          <h2>Why SpringUp</h2>
          <p>What sets our engagement approach apart</p>
        </motion.div>

        <motion.div
          className="em-why-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
          variants={stagger}
        >
          {whyItems.map((item) => {
            const Icon = item.icon;
            return (
              <motion.div key={item.title} className="em-why-card" variants={fadeUp}>
                <div className="em-why-icon">
                  <Icon />
                </div>
                <h4>{item.title}</h4>
                <p>{item.desc}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </section> */}

      {/* CTA */}
      <motion.section
        className="em-cta"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={stagger}
      >
        <motion.h2 variants={fadeUp}>
          Ready to Find the Right Engagement Model?
        </motion.h2>
        <motion.p variants={fadeUp}>
          Let's discuss how our flexible engagement models can accelerate your
          digital transformation journey.
        </motion.p>
        <motion.div variants={fadeUp}>
          <Link to="/contact" className="em-cta-btn text-decoration-none">
            Get Started <FiArrowRight size={18} />
          </Link>
        </motion.div>
      </motion.section>
    </div>
  );
};

export default EngagementModels;
